import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, InputGroup, Table, Spinner, Alert } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';

const MOSearchModal = ({ show, onHide, onSelectMO }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPendingLoading, setIsPendingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNoResultsAlert, setShowNoResultsAlert] = useState(false);
  const { instance } = useMsal();

  // Function to handle the MO search
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      return;
    }    // Process search term - remove "MO" prefix if present and handle spaces
    const processedSearchTerm = searchTerm.trim().replace(/^MO\s*/i, "");

    setIsLoading(true);
    setError(null);
    setShowNoResultsAlert(false);

    try {
      // Get the signed-in user account
      const accounts = instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        throw new Error('No signed-in user account found. Please sign in first.');
      }
      const account = accounts[0];

      console.log("MO Search - Using account:", account.username);

      // Define the authentication request with the correct scopes for Microsoft Graph API
      const request = {
        scopes: ["https://graph.microsoft.com/.default"],
        account: account,
      };

      // Acquire a token silently using the account
      console.log("MO Search - Acquiring token...");
      const tokenResponse = await instance.acquireTokenSilent(request);
      
      if (!tokenResponse || !tokenResponse.accessToken) {
        throw new Error('Failed to acquire access token. Token response is empty or invalid.');
      }
      
      const accessToken = tokenResponse.accessToken;
      console.log("MO Search - Token acquired successfully");

      // Get SharePoint site and list IDs from environment variables
      const siteId = process.env.REACT_APP_MANUFACTUREORDERS_SITE_ID;
      let listId = process.env.REACT_APP_MO_LIST_ID || '';
      let jobNameListId = process.env.REACT_APP_JOBNAME_LIST_ID || '';
      
      // If the list IDs contain encoded curly braces, decode them
      if (listId.includes('%7B')) {
        listId = decodeURIComponent(listId);
      }
      
      if (jobNameListId.includes('%7B')) {
        jobNameListId = decodeURIComponent(jobNameListId);
      }
      
      // If the list IDs have curly braces, remove them for the Graph API
      if (listId.startsWith('{') && listId.endsWith('}')) {
        listId = listId.substring(1, listId.length - 1);
      }
      
      if (jobNameListId.startsWith('{') && jobNameListId.endsWith('}')) {
        jobNameListId = jobNameListId.substring(1, jobNameListId.length - 1);
      }

      if (!siteId || !listId || !jobNameListId) {
        throw new Error('SharePoint site ID or list IDs not configured. Please check your environment variables.');
      }

      // Using the exact URL format provided from Graph Explorer - with the processed search term (without MO prefix)
      const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items/${processedSearchTerm}?$expand=fields`;
      
      console.log("MO Search - Making request to:", graphUrl);
      
      // Fetch items from SharePoint list with server-side filtering
      const response = await axios.get(graphUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: "application/json"
        },
      });

      console.log("MO Search - Response received:", response.status);
      console.log("MO Search - Response data:", response.data);

      // Try to fetch user data using SharePoint REST API with proper authentication
      let userLookupMap = {};
      try {
        console.log("MO Search - Attempting to fetch SharePoint users using REST API...");
        
        // Acquire a token specifically for SharePoint
        const sharePointRequest = {
          scopes: ["https://watertronics.sharepoint.com/.default"],
          account: account,
        };
        
        const sharePointTokenResponse = await instance.acquireTokenSilent(sharePointRequest);
        
        if (sharePointTokenResponse && sharePointTokenResponse.accessToken) {
          const sharePointToken = sharePointTokenResponse.accessToken;
          console.log("MO Search - SharePoint token acquired successfully");
          
          const userDataUrl = "https://watertronics.sharepoint.com/sites/manufacturingorders/_api/web/siteusers/";
          
          const userResponse = await axios.get(userDataUrl, {
            headers: {
              Authorization: `Bearer ${sharePointToken}`,
              Accept: "application/json;odata=verbose"
            },
          });

          if (userResponse.data && userResponse.data.d && userResponse.data.d.results) {
            // Create a lookup map from user ID to user title
            userResponse.data.d.results.forEach(user => {
              userLookupMap[user.Id] = user.Title;
            });
            console.log(`MO Search - SharePoint user lookup map created with ${Object.keys(userLookupMap).length} users`);
          }
        } else {
          console.log("MO Search - Could not acquire SharePoint token, falling back to Graph API approach");
          // Fallback to previous approach if SharePoint token fails
          const siteUsersUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/members`;
          
          const userResponse = await axios.get(siteUsersUrl, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json"
            },
          });

          if (userResponse.data && userResponse.data.value) {
            userResponse.data.value.forEach(user => {
              if (user.id && user.displayName) {
                userLookupMap[user.id] = user.displayName;
              }
            });
            console.log(`MO Search - Fallback Graph API lookup map created with ${Object.keys(userLookupMap).length} users`);
          }
        }
      } catch (userError) {
        console.error("MO Search - Error fetching user data:", userError.response?.status, userError.response?.statusText);
        // Continue without user data - this is expected since SharePoint lookup IDs are complex to resolve
      }

      // Process the response data
      let items = [];
      
      // When fetching by ID directly, the response structure is different from a collection
      if (response.data && response.data.id && response.data.fields) {
        // For direct item requests, the item data is in the response.data object itself
        console.log(`MO Search - Single item found with ID: ${response.data.id}`);
        
        // Check if we need to fetch job name details
        const jobNameLookupId = response.data.fields.Job_x0020_nameLookupId;
        const assignedLookupId = response.data.fields.AssignedLookupId;
        
        if (jobNameLookupId) {
          console.log(`MO Search - Found Job Name Lookup ID: ${jobNameLookupId}, fetching details...`);
          
          try {
            // Fetch the job name details using the lookup ID
            const jobNameUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${jobNameListId}/items/${jobNameLookupId}?$expand=fields`;
            
            const jobNameResponse = await axios.get(jobNameUrl, {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: "application/json"
              },
            });
            
            if (jobNameResponse.data && jobNameResponse.data.fields) {
              console.log(`MO Search - Job name details retrieved successfully for ID: ${jobNameLookupId}`);
              
              // Augment the MO item with job name data and user name
              const enrichedItem = {
                ...response.data,
                jobNameDetails: jobNameResponse.data,
                // Extract key fields for easier access in the UI
                fields: {
                  ...response.data.fields,
                  // If the title field contains the job name, use it
                  JobName: jobNameResponse.data.fields.Title || 'Unknown Job',
                  // Map AssignedLookupId to user title
                  AssignedUserName: assignedLookupId && userLookupMap[assignedLookupId] ? userLookupMap[assignedLookupId] : undefined
                }
              };
              
              items = [enrichedItem];
            } else {
              console.warn(`MO Search - Could not retrieve job name details for ID: ${jobNameLookupId}`);
              // Still add user name mapping even without job details
              const enrichedItem = {
                ...response.data,
                fields: {
                  ...response.data.fields,
                  AssignedUserName: assignedLookupId && userLookupMap[assignedLookupId] ? userLookupMap[assignedLookupId] : undefined
                }
              };
              items = [enrichedItem];
            }
          } catch (jobNameError) {
            console.error(`MO Search - Error fetching job name details for ID: ${jobNameLookupId}`, jobNameError);
            // Still include the original item with user name mapping even if we couldn't get job details
            const enrichedItem = {
              ...response.data,
              fields: {
                ...response.data.fields,
                AssignedUserName: assignedLookupId && userLookupMap[assignedLookupId] ? userLookupMap[assignedLookupId] : undefined
              }
            };
            items = [enrichedItem];
          }
        } else {
          console.log(`MO Search - No Job Name Lookup ID found, using only MO data with user mapping`);
          // Add user name mapping even without job details
          const enrichedItem = {
            ...response.data,
            fields: {
              ...response.data.fields,
              AssignedUserName: assignedLookupId && userLookupMap[assignedLookupId] ? userLookupMap[assignedLookupId] : undefined
            }
          };
          items = [enrichedItem];
        }
      } else if (response.data && response.data.value) {
        // For collection responses (though unlikely with this URL format)
        // Add user name mapping to each item
        items = response.data.value.map(item => ({
          ...item,
          fields: {
            ...item.fields,
            AssignedUserName: item.fields.AssignedLookupId && userLookupMap[item.fields.AssignedLookupId] ? userLookupMap[item.fields.AssignedLookupId] : undefined
          }
        }));
        console.log(`MO Search - Items found in collection: ${items.length}`);
      }
      
      setFilteredItems(items);
      setShowNoResultsAlert(items.length === 0);
      
    } catch (error) {
      console.error('Error searching for MOs:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'An error occurred while searching for MOs';
      
      if (error.response) {
        // Server responded with an error status
        console.error('Error response:', error.response);
        const responseData = error.response.data;
        
        if (responseData && responseData.error) {
          errorMessage = `${responseData.error.code}: ${responseData.error.message}`;
        } else {
          errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response received from server. Please check your connection.';
      } else if (error.message) {
        // Something else happened
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle finding pending MOs
  const handleFindPending = async () => {
    setIsPendingLoading(true);
    setError(null);
    setShowNoResultsAlert(false);

    try {
      // Get the signed-in user account
      const accounts = instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        throw new Error('No signed-in user account found. Please sign in first.');
      }
      const account = accounts[0];

      console.log("MO Find Pending - Using account:", account.username);

      // Define the authentication request with the correct scopes for Microsoft Graph API
      const request = {
        scopes: ["https://graph.microsoft.com/.default"],
        account: account,
      };

      // Acquire a token silently using the account
      console.log("MO Find Pending - Acquiring token...");
      const tokenResponse = await instance.acquireTokenSilent(request);
      
      if (!tokenResponse || !tokenResponse.accessToken) {
        throw new Error('Failed to acquire access token. Token response is empty or invalid.');
      }
      
      const accessToken = tokenResponse.accessToken;
      console.log("MO Find Pending - Token acquired successfully");

      // Get SharePoint site and list IDs from environment variables
      const siteId = process.env.REACT_APP_MANUFACTUREORDERS_SITE_ID;
      let listId = process.env.REACT_APP_MO_LIST_ID || '';
      let jobNameListId = process.env.REACT_APP_JOBNAME_LIST_ID || '';
      
      // If the list IDs contain encoded curly braces, decode them
      if (listId.includes('%7B')) {
        listId = decodeURIComponent(listId);
      }
      
      if (jobNameListId.includes('%7B')) {
        jobNameListId = decodeURIComponent(jobNameListId);
      }
      
      // If the list IDs have curly braces, remove them for the Graph API
      if (listId.startsWith('{') && listId.endsWith('}')) {
        listId = listId.substring(1, listId.length - 1);
      }
      
      if (jobNameListId.startsWith('{') && jobNameListId.endsWith('}')) {
        jobNameListId = jobNameListId.substring(1, jobNameListId.length - 1);
      }

      if (!siteId || !listId || !jobNameListId) {
        throw new Error('SharePoint site ID or list IDs not configured. Please check your environment variables.');
      }

      // Build the Graph URL to get all items with expanded fields - we'll filter client-side
      let nextLink = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listId}/items?$expand=fields`;
      
      console.log("MO Find Pending - Starting pagination from:", nextLink);
      
      let allItems = [];

      // Handle pagination by following @odata.nextLink until all items are retrieved
      while (nextLink) {
        console.log(`MO Find Pending - Fetching items from: ${nextLink}`);
        
        const response = await axios.get(nextLink, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json"
          },
        });

        console.log(`MO Find Pending - Response received: ${response.status}`);
        const responseData = response.data;

        // Add fetched items to our collection
        if (Array.isArray(responseData.value)) {
          allItems = allItems.concat(responseData.value);
          console.log(`MO Find Pending - Retrieved ${responseData.value.length} items, total so far: ${allItems.length}`);
        }

        // Check if there are more pages to fetch
        nextLink = responseData["@odata.nextLink"] || null;
        if (nextLink) {
          console.log(`MO Find Pending - Following next link for more results...`);
        }
      }

      console.log(`MO Find Pending - Total items retrieved: ${allItems.length}`);

      // Filter items to only include those with "Scheduling active" status
      const filteredItems = allItems.filter(item => 
        item.fields && item.fields.MO_x0020_status === 'Scheduling active'
      );

      console.log(allItems)
      
      console.log(`MO Find Pending - Items after filtering for "Scheduling active": ${filteredItems.length}`);

      // Sort filtered items by ID in descending order (most recent first)
      const sortedItems = filteredItems.sort((a, b) => {
        const idA = parseInt(a.id) || 0;
        const idB = parseInt(b.id) || 0;
        return idB - idA; // Descending order
      });

      console.log(`MO Find Pending - Items sorted by ID descending`);

      // Try to fetch user data using SharePoint REST API with proper authentication
      let userLookupMap = {};
      try {
        console.log("MO Find Pending - Attempting to fetch SharePoint users using REST API...");
        
        // Acquire a token specifically for SharePoint
        const sharePointRequest = {
          scopes: ["https://watertronics.sharepoint.com/.default"],
          account: account,
        };
        
        const sharePointTokenResponse = await instance.acquireTokenSilent(sharePointRequest);
        
        if (sharePointTokenResponse && sharePointTokenResponse.accessToken) {
          const sharePointToken = sharePointTokenResponse.accessToken;
          console.log("MO Find Pending - SharePoint token acquired successfully");
          
          const userDataUrl = "https://watertronics.sharepoint.com/sites/manufacturingorders/_api/web/siteusers/";
          
          const userResponse = await axios.get(userDataUrl, {
            headers: {
              Authorization: `Bearer ${sharePointToken}`,
              Accept: "application/json;odata=verbose"
            },
          });

          if (userResponse.data && userResponse.data.d && userResponse.data.d.results) {
            // Create a lookup map from user ID to user title
            userResponse.data.d.results.forEach(user => {
              userLookupMap[user.Id] = user.Title;
            });
            console.log(`MO Find Pending - SharePoint user lookup map created with ${Object.keys(userLookupMap).length} users`);
          }
        } else {
          console.log("MO Find Pending - Could not acquire SharePoint token, falling back to Graph API approach");
          // Fallback to previous approach if SharePoint token fails
          const siteUsersUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/members`;
          
          const userResponse = await axios.get(siteUsersUrl, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: "application/json"
            },
          });

          if (userResponse.data && userResponse.data.value) {
            userResponse.data.value.forEach(user => {
              if (user.id && user.displayName) {
                userLookupMap[user.id] = user.displayName;
              }
            });
            console.log(`MO Find Pending - Fallback Graph API lookup map created with ${Object.keys(userLookupMap).length} users`);
          }
        }
      } catch (userError) {
        console.error("MO Find Pending - Error fetching user data:", userError.response?.status, userError.response?.statusText);
        // Continue without user data - this is expected since SharePoint lookup IDs are complex to resolve
      }

      // Enrich sorted items with job name details and user names
      const enrichedItems = await Promise.all(
        sortedItems.map(async (item) => {
          const jobNameLookupId = item.fields.Job_x0020_nameLookupId;
          const assignedLookupId = item.fields.AssignedLookupId;
          
          let enrichedItem = { ...item };
          
          // Fetch job name details
          if (jobNameLookupId) {
            try {
              console.log(`MO Find Pending - Fetching job name details for MO ${item.id}, Job Name Lookup ID: ${jobNameLookupId}`);
              
              const jobNameUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${jobNameListId}/items/${jobNameLookupId}?$expand=fields`;
              
              const jobNameResponse = await axios.get(jobNameUrl, {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  Accept: "application/json"
                },
              });
              
              if (jobNameResponse.data && jobNameResponse.data.fields) {
                enrichedItem = {
                  ...enrichedItem,
                  jobNameDetails: jobNameResponse.data,
                  fields: {
                    ...enrichedItem.fields,
                    JobName: jobNameResponse.data.fields.Title || 'Unknown Job'
                  }
                };
              }
            } catch (jobNameError) {
              console.error(`MO Find Pending - Error fetching job name details for MO ${item.id}:`, jobNameError);
            }
          }
          
          // Map AssignedLookupId to user title
          if (assignedLookupId && userLookupMap[assignedLookupId]) {
            enrichedItem.fields = {
              ...enrichedItem.fields,
              AssignedUserName: userLookupMap[assignedLookupId]
            };
          }
          
          return enrichedItem;
        })
      );
      
      setFilteredItems(enrichedItems);
      setShowNoResultsAlert(enrichedItems.length === 0);
      
    } catch (error) {
      console.error('Error finding pending MOs:', error);
      
      // Extract detailed error message if available
      let errorMessage = 'An error occurred while finding pending MOs';
      
      if (error.response) {
        // Server responded with an error status
        console.error('Error response:', error.response);
        const responseData = error.response.data;
        
        if (responseData && responseData.error) {
          errorMessage = `${responseData.error.code}: ${responseData.error.message}`;
        } else {
          errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response received from server. Please check your connection.';
      } else if (error.message) {
        // Something else happened
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsPendingLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };
  const handleSelectItem = (item) => {
    // Transform MO data to job data format before passing to parent component
    const jobData = {
      JobNumber: `MO${item.id}`,
      ProjectName: item.fields.JobName || '',  // This comes from the enriched data with job name details
      ModelNumber: item.fields.MOname || '',
      JobType: item.fields.Site_x0020_status === "Emergency Rush" ? "High Priority" : "",
      DollarValue: item.fields.Sell_x0024__x0024_ || '',
      OrderReceived: item.fields.Created ? new Date(item.fields.Created).toLocaleDateString() : '', // Format as date without time,
      // Add engineering requirements data
      MechanicalEngineering: item.fields.MechanicalEngineering || '',
      ElectricalEngineering: item.fields.ElectricalEngineering || '',
      ProgrammingRequired: item.fields.ProgrammingRequired_x003f_ || '',
      // Flag to indicate this job was created from MO search
      fromMOSearch: true
    }
      
    console.log('Selected MO item for job creation:', item);
    console.log('Transformed job data:', jobData);
    
    onSelectMO(jobData);
    onHide();
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Clear search results when modal is closed
  useEffect(() => {
    if (!show) {
      setSearchTerm('');
      setFilteredItems([]);
      setShowNoResultsAlert(false);
      setError(null);
    }
  }, [show]);

  return (
    <Modal className="job-schedule-modal" show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Search Manufacturing Orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                placeholder="Search by MO number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <Button 
                variant="primary" 
                onClick={handleSearch}
                disabled={isLoading || isPendingLoading || !searchTerm.trim()}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : "Search"}
              </Button>
            </InputGroup>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Text className="text-muted">
                Search for manufacturing orders to populate a new job.
              </Form.Text>
              <Button 
                variant="outline-primary" 
                onClick={handleFindPending}
                disabled={isLoading || isPendingLoading}
                size="sm"
              >
                {isPendingLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </>
                ) : (
                  "Find Pending"
                )}
              </Button>
            </div>
          </Form.Group>
        </Form>

        {error && (
          <Alert variant="danger">
            <Alert.Heading>Error:</Alert.Heading>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              Please verify that you have the correct permissions to access the SharePoint list
              and that the environment variables are properly configured.
            </p>
          </Alert>
        )}

        {showNoResultsAlert && (
          <Alert variant="info">
            {searchTerm ? 
              `No manufacturing orders found matching "${searchTerm}".` : 
              'No pending manufacturing orders found with "Scheduling active" status.'
            }
          </Alert>
        )}

        {filteredItems.length > 0 && (
          <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped bordered hover className="schedule-responsive-table">
              <thead>
                <tr>
                  <th>MO Number</th>
                  <th>MO Name</th>
                  <th>Job Name</th>
                  <th>Priority</th>
                  <th>RSM</th>
                  <th>Sell Price</th>
                  <th>Mo Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td data-label="MO Number">MO{item.id}</td>
                    <td data-label="MO Name">{item.fields.MOname}</td>
                    <td data-label="Job Name">{item.fields.JobName || (item.jobNameDetails?.fields?.Title || 'Unknown')}</td>
                    <td data-label="Priority" className={item.fields.Site_x0020_status === "Emergency Rush" ? "text-danger fw-bold" : ""}>
                      {item.fields.Site_x0020_status}
                    </td>
                    <td data-label="RSM">{item.fields.AssignedUserName || (item.fields.AssignedLookupId ? `User ID: ${item.fields.AssignedLookupId}` : "N/A")}</td>
                    <td data-label="Sell Price">{formatCurrency(item.fields.Sell_x0024__x0024_)}</td>
                    <td data-label="MO Status">{item.fields.MO_x0020_status}</td>
                    <td data-label="Actions">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleSelectItem(item)}
                        className="schedule-card-action mo-search-select-button"
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

MOSearchModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSelectMO: PropTypes.func.isRequired,
};

export default MOSearchModal;
